// File: src/components/Prescriptions/PrescriptionBanner.jsx
// Clean, minimal banner showing prescription title + description only

import React from 'react';
import { Icon } from '@shopify/polaris';
import { 
  AlertCircleIcon,    // critical
  AlertTriangleIcon,  // high  
  LightbulbIcon,      // medium
  InfoIcon            // low
} from '@shopify/polaris-icons';
import useData from '../../hooks/useData';
import './PrescriptionBanner.css';

const URGENCY_ICONS = {
  critical: AlertCircleIcon,
  high: AlertTriangleIcon,
  medium: LightbulbIcon,
  low: InfoIcon,
};

const URGENCY_COLORS = {
  critical: '#dc2626',
  high: '#f59e0b',
  medium: '#6A0DAD',
  low: '#00A8A9',
};

export default function PrescriptionBanner({ 
  siteId, 
  token, 
  category = null,
}) {
  const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';
  
  // Build query params
  const queryParams = new URLSearchParams({ site_id: siteId, status: 'active' });
  if (category) queryParams.append('category', category);
  
  // Fetch prescriptions
  const { data: prescriptions, loading } = useData(
    siteId ? `prescription-banner:${siteId}:${category}` : null,
    () => fetch(`${API}/api/prescriptions?${queryParams}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : []),
    { ttlMs: 2 * 60 * 1000, deps: [siteId, category] }
  );
  
  // Don't render if no site, loading, or no prescriptions
  if (!siteId || loading || !prescriptions || !Array.isArray(prescriptions) || prescriptions.length === 0) {
    return null;
  }
  
  // Get highest priority prescription
  const prescription = prescriptions.sort((a, b) => {
    const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    return (urgencyOrder[a.urgency] || 3) - (urgencyOrder[b.urgency] || 3);
  })[0];
  
  const IconSource = URGENCY_ICONS[prescription.urgency] || LightbulbIcon;
  const iconColor = URGENCY_COLORS[prescription.urgency] || '#6A0DAD';
  
  return (
    <div className={`prescription-banner prescription-banner--${prescription.urgency}`}>
      <div className="prescription-banner__content">
        <div className="prescription-banner__title">
          <span className="prescription-banner__icon" style={{ color: iconColor }}>
            <Icon source={IconSource} />
          </span>
          {prescription.title}
        </div>
        <div className="prescription-banner__description">
          {prescription.description}
        </div>
      </div>
      <div className="prescription-banner__accent" />
    </div>
  );
}